import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, DollarSign, TrendingUp, TrendingDown, Clock, Eye, ArrowLeft,
  User, Building2, Shield, CheckCircle, X, ExternalLink, FileText, Hash,
  ArrowRight, Calendar, AlertTriangle, Banknote, Tag
} from "lucide-react";
import { LuxuryBackground, Card, GoldButton, PageHero } from "../components/shared/UIComponents";
import { fmtUSD } from "../utils/currency";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  active: { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", label: "ACTIVE" },
  sold: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200", label: "SOLD" },
  cancelled: { bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-200", label: "CANCELLED" },
};

export default function Listing360Page({ entityId, onBack }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/entity/listing/${entityId}/360`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [entityId]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
  );

  if (!data) return (
    <div className="flex flex-col items-center justify-center min-h-screen text-[#888]">
      <AlertTriangle className="h-12 w-12 mb-3 opacity-30" />
      <p>Listing not found</p>
    </div>
  );

  const { listing, seller, buyer, commitment, opportunity, deal, transaction } = data;
  const sty = STATUS_STYLES[listing.status] || STATUS_STYLES.active;
  const priceDelta = listing.price_change_pct || 0;

  return (
    <div className="relative" data-testid="listing-360-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-950 via-emerald-900 to-[#022c22] text-cream px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <button onClick={onBack} className="flex items-center gap-2 text-sm text-gold hover:text-gold/80 transition-colors mb-6" data-testid="listing-360-back">
            <ArrowLeft className="h-4 w-4" /> Back to Market
          </button>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-2">Secondary Market Listing</p>
              <h1 className="text-3xl md:text-4xl font-bold mb-2" data-testid="listing-360-title">
                {(opportunity?.property_address || deal?.property_address || `Listing ${entityId.slice(0, 8)}`)}
              </h1>
              <div className="flex items-center gap-3 mt-3">
                <span className={`px-3 py-1 rounded-full text-[11px] font-bold ${sty.bg} ${sty.text} ${sty.border} border`}>{sty.label}</span>
                {opportunity?.loan_type_label && <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-white/15 text-cream/80 border border-white/20">{opportunity.loan_type_label}</span>}
                {(opportunity?.state || deal?.state) && <span className="text-xs text-cream/60">{opportunity?.state || deal?.state}</span>}
                {deal?.deal_number && <span className="text-xs text-gold/70">{deal.deal_number}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black text-gold">{fmtUSD(listing.asking_price || 0)}</div>
              <div className="text-xs text-cream/60 mt-1">Asking Price</div>
              {priceDelta !== 0 && (
                <div className={`flex items-center justify-end gap-1 mt-1 text-xs font-medium ${priceDelta < 0 ? "text-emerald-300" : "text-amber-300"}`}>
                  {priceDelta < 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                  {Math.abs(priceDelta)}% {priceDelta < 0 ? "discount" : "premium"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6">

            {/* Left Column (2 cols) */}
            <div className="lg:col-span-2 space-y-6">

              {/* Pricing 360 */}
              <Card hover={false}>
                <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-emerald-600" /> Pricing Overview
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm text-center">
                    <div className="text-lg font-bold text-[#0A0A0A]">{fmtUSD(listing.original_amount || 0)}</div>
                    <div className="text-[10px] text-[#888]">Original Amount</div>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                    <div className="text-lg font-bold text-emerald-700">{fmtUSD(listing.asking_price || 0)}</div>
                    <div className="text-[10px] text-[#888]">Asking Price</div>
                  </div>
                  {listing.sale_price != null && (
                    <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-center">
                      <div className="text-lg font-bold text-blue-700">{fmtUSD(listing.sale_price || 0)}</div>
                      <div className="text-[10px] text-[#888]">Sale Price</div>
                    </div>
                  )}
                  <div className={`p-3 rounded-xl border text-center ${priceDelta < 0 ? "bg-emerald-50 border-emerald-200" : priceDelta > 0 ? "bg-amber-50 border-amber-200" : "bg-[#faf7f2] border-cream-warm"}`}>
                    <div className={`text-lg font-bold ${priceDelta < 0 ? "text-emerald-600" : priceDelta > 0 ? "text-amber-600" : "text-[#333]"}`}>
                      {priceDelta > 0 ? "+" : ""}{priceDelta}%
                    </div>
                    <div className="text-[10px] text-[#888]">{priceDelta < 0 ? "Discount" : priceDelta > 0 ? "Premium" : "At Par"}</div>
                  </div>
                </div>
                {listing.remaining_term_months && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-[#666]">
                    <Clock className="h-4 w-4 text-[#999]" /> {listing.remaining_term_months} months remaining
                  </div>
                )}
              </Card>

              {/* Underlying Opportunity 360 */}
              {opportunity && (
                <Card hover={false}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-blue-600" /> Underlying Opportunity
                    </h3>
                    {onEntityNavigate && (
                      <button onClick={() => navigateToEntity("opportunity", opportunity.opportunity_id)}
                        className="flex items-center gap-1 text-[10px] font-medium text-emerald-700 hover:text-emerald-600 transition-colors"
                        data-testid="link-to-opp-360">
                        <ExternalLink className="h-3 w-3" /> View Opportunity 360
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {opportunity.property_address && (
                      <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm col-span-2">
                        <div className="text-[10px] text-[#888] mb-0.5">Property</div>
                        <div className="text-sm font-semibold text-[#0A0A0A]">{opportunity.property_address}</div>
                      </div>
                    )}
                    {opportunity.target_raise && (
                      <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm text-center">
                        <div className="text-sm font-bold text-[#0A0A0A]">{fmtUSD(opportunity.target_raise)}</div>
                        <div className="text-[10px] text-[#888]">Target Raise</div>
                      </div>
                    )}
                    {opportunity.projected_yield && (
                      <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                        <div className="text-sm font-bold text-emerald-700">{opportunity.projected_yield}%</div>
                        <div className="text-[10px] text-[#888]">Projected Yield</div>
                      </div>
                    )}
                    {opportunity.projected_term_months && (
                      <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm text-center">
                        <div className="text-sm font-bold text-[#0A0A0A]">{opportunity.projected_term_months}mo</div>
                        <div className="text-[10px] text-[#888]">Term</div>
                      </div>
                    )}
                    {opportunity.status && (
                      <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm text-center">
                        <div className="text-sm font-bold text-[#0A0A0A] capitalize">{opportunity.status}</div>
                        <div className="text-[10px] text-[#888]">Status</div>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Underlying Deal 360 */}
              {deal && (
                <Card hover={false}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-600" /> Underlying Deal
                    </h3>
                    {onEntityNavigate && deal.lead_id && (
                      <button onClick={() => navigateToEntity("deal", deal.lead_id)}
                        className="flex items-center gap-1 text-[10px] font-medium text-emerald-700 hover:text-emerald-600 transition-colors"
                        data-testid="link-to-deal-360">
                        <ExternalLink className="h-3 w-3" /> View Deal 360
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {deal.deal_number && (
                      <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm">
                        <div className="text-[10px] text-[#888] mb-0.5">Deal #</div>
                        <div className="text-sm font-bold text-emerald-700">{deal.deal_number}</div>
                      </div>
                    )}
                    {deal.purchase_price && (
                      <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm text-center">
                        <div className="text-sm font-bold text-[#0A0A0A]">{fmtUSD(deal.purchase_price)}</div>
                        <div className="text-[10px] text-[#888]">Purchase Price</div>
                      </div>
                    )}
                    {deal.arv && (
                      <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm text-center">
                        <div className="text-sm font-bold text-[#0A0A0A]">{fmtUSD(deal.arv)}</div>
                        <div className="text-[10px] text-[#888]">ARV</div>
                      </div>
                    )}
                    {deal.stage && (
                      <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm text-center">
                        <div className="text-sm font-bold text-[#0A0A0A] capitalize">{deal.stage}</div>
                        <div className="text-[10px] text-[#888]">Stage</div>
                      </div>
                    )}
                    {deal.borrower_name && (
                      <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm col-span-2">
                        <div className="text-[10px] text-[#888] mb-0.5">Borrower</div>
                        <div className="text-sm font-medium text-[#333]">{deal.borrower_name}</div>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Transaction Record */}
              {transaction && (
                <Card hover={false}>
                  <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" /> Transaction Record
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-center">
                      <div className="text-sm font-bold text-blue-700">{fmtUSD(transaction.sale_price || 0)}</div>
                      <div className="text-[10px] text-[#888]">Sale Price</div>
                    </div>
                    <div className="p-3 rounded-xl bg-[#faf7f2] border border-cream-warm text-center">
                      <div className="text-sm font-bold text-[#0A0A0A]">{fmtUSD(transaction.original_amount || 0)}</div>
                      <div className="text-[10px] text-[#888]">Original Amount</div>
                    </div>
                    <div className={`p-3 rounded-xl border text-center ${transaction.price_change_pct < 0 ? "bg-emerald-50 border-emerald-200" : transaction.price_change_pct > 0 ? "bg-amber-50 border-amber-200" : "bg-[#faf7f2] border-cream-warm"}`}>
                      <div className={`text-sm font-bold ${transaction.price_change_pct < 0 ? "text-emerald-600" : transaction.price_change_pct > 0 ? "text-amber-600" : "text-[#333]"}`}>
                        {transaction.price_change_pct > 0 ? "+" : ""}{transaction.price_change_pct}%
                      </div>
                      <div className="text-[10px] text-[#888]">Price Change</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-[#888]">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Completed {new Date(transaction.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                </Card>
              )}
            </div>

            {/* Right Column (Sidebar) */}
            <div className="space-y-6">

              {/* Listing Meta */}
              <Card hover={false}>
                <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Listing Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#888]">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sty.bg} ${sty.text}`}>{sty.label}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#888]">Views</span>
                    <span className="flex items-center gap-1 font-medium text-[#333]"><Eye className="h-3 w-3" /> {listing.views || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#888]">Listed</span>
                    <span className="text-[#333]">{listing.created_at ? new Date(listing.created_at).toLocaleDateString() : "—"}</span>
                  </div>
                  {listing.sold_at && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#888]">Sold</span>
                      <span className="text-[#333]">{new Date(listing.sold_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {listing.notes && (
                    <div className="mt-3 p-3 rounded-xl bg-[#faf7f2] border border-cream-warm">
                      <div className="text-[10px] text-[#888] mb-1">Seller Notes</div>
                      <p className="text-sm text-[#555]">{listing.notes}</p>
                    </div>
                  )}
                </div>
              </Card>

              {/* Seller Info */}
              <Card hover={false}>
                <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <User className="h-4 w-4 text-emerald-600" /> Seller
                </h3>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center text-sm font-bold text-white">
                    {(seller.full_name || "?").charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0A0A0A]">{seller.full_name || "Investor"}</div>
                    {seller.email && <div className="text-xs text-[#888]">{seller.email}</div>}
                  </div>
                </div>
                {onEntityNavigate && seller.user_id && (
                  <button onClick={() => navigateToEntity("investor", seller.user_id)}
                    className="mt-3 flex items-center gap-1 text-[10px] font-medium text-emerald-700 hover:text-emerald-600 transition-colors"
                    data-testid="link-to-seller-360">
                    <ExternalLink className="h-3 w-3" /> View Investor 360
                  </button>
                )}
              </Card>

              {/* Buyer Info (if sold) */}
              {buyer && (
                <Card hover={false}>
                  <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <User className="h-4 w-4 text-blue-600" /> Buyer
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-sm font-bold text-white">
                      {(buyer.full_name || "?").charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-[#0A0A0A]">{buyer.full_name || "Investor"}</div>
                      {buyer.email && <div className="text-xs text-[#888]">{buyer.email}</div>}
                    </div>
                  </div>
                  {onEntityNavigate && buyer.user_id && (
                    <button onClick={() => navigateToEntity("investor", buyer.user_id)}
                      className="mt-3 flex items-center gap-1 text-[10px] font-medium text-emerald-700 hover:text-emerald-600 transition-colors"
                      data-testid="link-to-buyer-360">
                      <ExternalLink className="h-3 w-3" /> View Investor 360
                    </button>
                  )}
                </Card>
              )}

              {/* Commitment 360 */}
              {commitment && (
                <Card hover={false}>
                  <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Banknote className="h-4 w-4 text-gold-dark" /> Commitment Security
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#888]">Amount</span>
                      <span className="font-bold text-[#0A0A0A]">{fmtUSD(commitment.amount || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#888]">Status</span>
                      <span className="font-medium text-[#333] capitalize">{commitment.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#888]">Created</span>
                      <span className="text-[#333]">{commitment.created_at ? new Date(commitment.created_at).toLocaleDateString() : "—"}</span>
                    </div>
                  </div>
                  {onEntityNavigate && commitment.commitment_id && (
                    <button onClick={() => navigateToEntity("commitment", commitment.commitment_id)}
                      className="mt-3 flex items-center gap-1 text-[10px] font-medium text-emerald-700 hover:text-emerald-600 transition-colors"
                      data-testid="link-to-commitment-360">
                      <ExternalLink className="h-3 w-3" /> View Commitment 360
                    </button>
                  )}
                </Card>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
